/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: This file was automatically migrated to TypeScript by `dev typescript migrate`.
//  The eslint disable comment above should be removed and the resulting errors fixed.
import {gql, DocumentNode} from '@apollo/client';
import React from 'react';

import * as OrganizationActions from 'actions/OrganizationActions';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import * as colors from 'lib/Colors';
import navigateToURL from 'lib/navigateToURL';
import notifications from 'notifications';
import {Member, Team} from 'orgs/OrganizationConstants';
import TenantBreadcrumbs from 'tenants/TenantBreadcrumbs';
import Avatar from 'ui-library/avatar/Avatar';
import {AvatarShape, AvatarSize} from 'ui-library/avatar/AvatarConstants';
import {Header1} from 'ui-library/Headers';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as nyc from 'ui-library/NYC';
import SectionTabs from 'ui-library/SectionTabs';

import {TeamPageQuery, TeamPageQueryVariables} from './__generated/TeamPageQuery';
import AddTeamMemberInput from './AddTeamMemberInput';
import * as OrganizationConstants from './OrganizationConstants';
import TeamBenchlingAppMembersTab from './TeamBenchlingAppMembersTab';
import TeamCalendarTab from './TeamCalendarTab';
import TeamMembersTab, {TEAM_MEMBERS_TEAM_FRAGMENT} from './TeamMembersTab';
import TeamProjectsTab from './TeamProjectsTab';
import TeamSettingsTab, {TEAM_SETTINGS_TEAM_FRAGMENT} from './TeamSettingsTab';

type TeamPageQueryTeam = ExtractByTypename<TeamPageQuery['team'], 'TeamNode'>;

export const TEAM_PAGE_QUERY: DocumentNode = gql`
  query TeamPageQuery($id: StrictString!) @engComponent(name: EXTERNAL_ADMIN) @skipAudit {
    team(id: $id) {
      ... on TeamNode {
        id
        avatarUrl
        handle
        name
        admin
        member
        organization {
          id
          admin
          avatarUrl
          member
          name
          urlIdentifier
          email
        }
        permissions {
          canManageRoles
        }
        ...TeamSettingsTeam
        ...TeamMembersTeam
      }
      ...InaccessibleInfoGraphQL
    }
    currentTenant {
      id
      permissions {
        admin
      }
    }
  }
  ${TEAM_SETTINGS_TEAM_FRAGMENT}
  ${TEAM_MEMBERS_TEAM_FRAGMENT}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const TEAM_TAB_TO_DISPLAY: {[key: string]: string} = {
  [OrganizationConstants.PROJECTS_TAB]: 'Projects',
  [OrganizationConstants.CALENDAR_TAB]: 'Calendar',
  [OrganizationConstants.MEMBERS_TAB]: 'Members',
  [OrganizationConstants.APPS_TAB]: 'Apps',
  [OrganizationConstants.SETTINGS_TAB]: 'Settings',
};

export interface TeamPageOrganization {
  admin: boolean;
  member: boolean;
  url_identifier: string;
  avatar_url: string;
  id: string;
  name: string;
  handle: string;
  email: string;
}
export interface TeamPageProps {
  activeTab: string;
  isLoading: boolean;
  deprecatedTeam: Team;
  team: TeamPageQueryTeam;
  teamProjects?: Array<any>;
  teamMembers?: Array<Member>;
  userIdToMemberInfo?: any;
  isCurrentUserTenantAdmin: boolean;
}

export class TeamPage extends React.Component<TeamPageProps> {
  _openOrganizationPage = (): void => {
    navigateToURL(`organizations/${this.props.team.organization.urlIdentifier}/teams`);
  };

  _handleOpenTab = (tab: any): void => {
    OrganizationActions.updateActiveTab(this.props.team.id, tab);
  };

  render(): JSX.Element {
    // Keep visible tabs in sync with start/views.py::team_page (/teams/<team_id>/<section>)
    const teamTabs = [];
    const isCurrentUserMember = this.props.team.member;
    const canManageRoles = this.props.team.permissions.canManageRoles ?? false;

    teamTabs.push(OrganizationConstants.PROJECTS_TAB);
    if (isCurrentUserMember) {
      teamTabs.push(OrganizationConstants.CALENDAR_TAB);
    }
    if (isCurrentUserMember || canManageRoles) {
      teamTabs.push(OrganizationConstants.MEMBERS_TAB);
    }
    if (BenchlingConfig.ENABLE_BENCHLING_APPS && (isCurrentUserMember || canManageRoles)) {
      teamTabs.push(OrganizationConstants.APPS_TAB);
    }
    if (isCurrentUserMember || this.props.team.permissions.admin) {
      teamTabs.push(OrganizationConstants.SETTINGS_TAB);
    }

    // TODO(T8204): Set team header color based on Avatar.
    const headerColor = colors.stringToColor(this.props.team.name, 0.9);
    const isCurrentUserTenantAdmin = this.props.isCurrentUserTenantAdmin;
    const isTenantAdminWithConsoleAccess =
      isCurrentUserTenantAdmin && BenchlingConfig.ENABLE_TENANT_ADMIN_CONSOLE;
    let userMembers: Array<OrganizationConstants.Member> | undefined;
    let benchAppMembers: Array<OrganizationConstants.Member> | undefined;
    if (this.props.teamMembers != null) {
      userMembers = this.props.teamMembers.filter((member) => member.type === ModelTypeConstants.USER);
      benchAppMembers = this.props.teamMembers.filter(
        (member) => member.type === ModelTypeConstants.BENCHLING_APP
      );
    }
    return (
      <nyc.Container size="lg">
        <nyc.Row
          className="teamPage-container"
          style={{
            backgroundColor: headerColor,
            borderBottom: `1px solid ${colors.stringToColor(this.props.team.name, 0.4)}`,
          }}
          dataTestAttrs={{component: 'TeamPage', element: 'root'}}
        >
          <nyc.Col lg={1} />
          <nyc.Col lg={21}>
            <TenantBreadcrumbs
              organization={{
                ...this.props.team.organization,
                url_identifier: this.props.team.organization.urlIdentifier,
              }}
            />
            <div className="u-vertical-center-items u-spacing-bottom">
              <Avatar size={AvatarSize.XLARGE} shape={AvatarShape.SQUARE} entity={this.props.team} />
              <div className="u-small-spacing-left">
                <Header1>{this.props.team.name}</Header1>
                <div className="teamPage-headerSubtitle">
                  {isTenantAdminWithConsoleAccess ? (
                    <span className="u-gray-darker">Team</span>
                  ) : (
                    <>
                      <span className="u-bold-font-weight">Organization </span>
                      <a className="teamPage-headerLink" onClick={this._openOrganizationPage}>
                        {this.props.team.organization.name}
                      </a>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="u-lr-split">
              <SectionTabs
                tabs={teamTabs.map((tabValue) => ({
                  handleClick: () => {
                    this._handleOpenTab(tabValue);
                  },
                  isActive: this.props.activeTab === tabValue,
                  text: TEAM_TAB_TO_DISPLAY[tabValue],
                }))}
              />
              <div className="organizationPage-topCta">
                {(() => {
                  switch (this.props.activeTab) {
                    case OrganizationConstants.MEMBERS_TAB:
                      if (!this.props.team.permissions.canManageRoles) {
                        return null;
                      }
                      return (
                        <AddTeamMemberInput
                          existingMembers={userMembers}
                          team={this.props.team}
                          organizationId={this.props.team.organization.id}
                          type={ModelTypeConstants.USER}
                        />
                      );
                    case OrganizationConstants.APPS_TAB:
                      if (!this.props.team.permissions.canManageRoles) {
                        return null;
                      }
                      return (
                        <AddTeamMemberInput
                          existingMembers={benchAppMembers}
                          team={this.props.team}
                          organizationId={this.props.team.organization.id}
                          type={ModelTypeConstants.BENCHLING_APP}
                        />
                      );
                    default:
                      return null;
                  }
                })()}
              </div>
            </div>
          </nyc.Col>
        </nyc.Row>
        <nyc.Row className="organizationPage-sectionContainer">
          <nyc.Col lg={1} />
          <nyc.Col lg={21}>
            <div className="u-spacing-top">
              {(() => {
                switch (this.props.activeTab) {
                  case OrganizationConstants.PROJECTS_TAB:
                    return (
                      <TeamProjectsTab
                        isCurrentUserMember={isCurrentUserMember}
                        teamId={this.props.team.id}
                        teamProjects={this.props.teamProjects}
                      />
                    );
                  case OrganizationConstants.CALENDAR_TAB:
                    return <TeamCalendarTab teamId={this.props.team.id} />;
                  case OrganizationConstants.MEMBERS_TAB:
                    return (
                      <TeamMembersTab
                        isCurrentUserMember={this.props.team.organization.member}
                        team={this.props.team}
                        teamMembers={userMembers}
                        userIdToMemberInfo={this.props.userIdToMemberInfo}
                      />
                    );
                  case OrganizationConstants.APPS_TAB:
                    return (
                      <TeamBenchlingAppMembersTab
                        isCurrentUserMember={this.props.team.organization.member}
                        team={this.props.team}
                        teamBenchlingAppMembers={benchAppMembers}
                        userIdToMemberInfo={this.props.userIdToMemberInfo}
                      />
                    );
                  case OrganizationConstants.SETTINGS_TAB:
                    return <TeamSettingsTab isLoading={this.props.isLoading} team={this.props.team} />;
                  default:
                    return null;
                }
              })()}
            </div>
          </nyc.Col>
        </nyc.Row>
      </nyc.Container>
    );
  }
}

export interface QueryingTeamPageProps {
  activeTab: string;
  deprecatedTeam: Team;
  isLoading: boolean;
  userIdToMemberInfo?: any;
  teamProjects?: Array<any>;
  teamMembers?: Array<Member>;
}

export default function QueryingTeamPage(props: QueryingTeamPageProps): JSX.Element | null {
  const result = useBenchlingQuery<TeamPageQuery, TeamPageQueryVariables>(TEAM_PAGE_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      id: props.deprecatedTeam.id,
    },
  });
  if (result.type === QueryResultType.ERROR) {
    notifications.error('Could not load team. Try reloading the page.');
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else {
    assert(!isInaccessible(result.data.team), 'Team is inaccessible');
    return (
      <TeamPage
        isCurrentUserTenantAdmin={result.data.currentTenant.permissions.admin}
        isLoading={props.isLoading}
        activeTab={props.activeTab}
        deprecatedTeam={props.deprecatedTeam}
        team={result.data.team}
        userIdToMemberInfo={props.userIdToMemberInfo}
        teamProjects={props.teamProjects}
        teamMembers={props.teamMembers}
      />
    );
  }
}
